import {
  Accordion,
  AccordionSummary,
  MenuItem,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import type { CustomColorPalette } from '../../../theme';

interface SectionStyledProps {
  hasBorder: boolean;
}

export const StyledAccordion = styled(Accordion, {
  shouldForwardProp: prop => prop !== 'hasBorder',
})<SectionStyledProps>(({ theme, hasBorder }) => {
  const palette = theme.customColorPalette;
  const isDarkMode = theme.palette.mode === 'dark';
  return {
    boxShadow: 'none',
    backgroundColor: 'transparent',
    '&:before': { display: 'none' },
    marginBottom: hasBorder ? '8px' : '0px',
    borderBottom: hasBorder
      ? `1px solid ${isDarkMode ? palette.gray[700] : palette.gray[200]}`
      : 'none',
    '&.Mui-expanded': { margin: '0 0 8px 0' },
  };
});

export const StyledAccordionSummary = styled(AccordionSummary)({
  minHeight: '32px',
  maxHeight: '32px',
  padding: '0 8px',
  '&.Mui-expanded': { minHeight: '32px', maxHeight: '32px' },
  '& .MuiAccordionSummary-content': {
    margin: '8px 0 4px 0',
    overflow: 'hidden',
  },
});

export const SectionTitle = styled(Typography)(({ theme }) => ({
  color: theme.customColorPalette.gray[500],
  fontFamily: 'Roboto',
  fontSize: '11px',
  fontWeight: 500,
  lineHeight: '16px',
  letterSpacing: '0.5px',
  textTransform: 'uppercase',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  maxWidth: '100%',
}));

export const StyledMenuItem = styled(MenuItem)(({ theme }) => {
  const palette = theme.customColorPalette;
  const isDarkMode = theme.palette.mode === 'dark';
  return {
    fontSize: '13px',
    fontFamily: 'Roboto',
    color: isDarkMode ? palette.gray[200] : palette.gray[700],
    gap: '8px',
    '&:hover': {
      backgroundColor: isDarkMode ? palette.gray[700] : palette.gray[100],
    },
  };
});

export const DeleteMenuItem = styled(MenuItem)(({ theme }) => {
  const isDarkMode = theme.palette.mode === 'dark';
  return {
    fontSize: '13px',
    fontFamily: 'Roboto',
    color: isDarkMode ? '#ef4444' : '#dc2626',
    gap: '8px',
    '&:hover': {
      backgroundColor: isDarkMode
        ? 'rgba(239,68,68,0.1)'
        : 'rgba(220,38,38,0.1)',
    },
  };
});

export const sectionMenuPaperSx = (
  isDarkMode: boolean,
  palette: CustomColorPalette,
) => ({
  padding: '8px',
  backgroundColor: isDarkMode ? palette.gray[900] : '#FFFFFF',
  minWidth: '180px',
  borderRadius: '8px',
  border: `1px solid ${isDarkMode ? palette.gray[700] : palette.gray[300]}`,
  boxShadow: 'none',
});

export const sectionIconButtonSx = (
  isDarkMode: boolean,
  palette: CustomColorPalette,
) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '4px',
  border: 'none',
  borderRadius: '4px',
  backgroundColor: 'transparent',
  cursor: 'pointer' as const,
  color: isDarkMode ? palette.gray[400] : palette.gray[500],
  marginRight: '2px',
  '&:hover': {
    backgroundColor: isDarkMode ? palette.gray[800] : palette.gray[200],
  },
});
